import React from "react";
import styled, { keyframes } from "styled-components/macro";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { FiChevronDown } from "react-icons/fi";
import logoRata from "../images/logoRata.png";
import RubberDuck from "./RubberDuck";
import DonationProgress from "./DonationProgress";
import {
  RACE_DATE_LABEL,
  RACE_EDITION,
  RACE_CITY,
} from "../data/EventConfig";

const Section = styled.section`
  position: relative;
  min-height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 7rem 1.5rem 9rem;
  background:
    radial-gradient(circle at 15% 20%, rgba(255, 206, 45, 0.55) 0%, transparent 45%),
    radial-gradient(circle at 85% 25%, rgba(250, 167, 30, 0.45) 0%, transparent 50%),
    linear-gradient(180deg, #fff8e1 0%, #ffeec2 60%, #ffd97a 100%);
  overflow: hidden;
  text-align: center;
`;

const wave = keyframes`
  0% { transform: translateX(0) translateY(0); }
  50% { transform: translateX(-25%) translateY(4px); }
  100% { transform: translateX(-50%) translateY(0); }
`;

const Wave = styled.svg`
  position: absolute;
  bottom: -1px;
  left: 0;
  width: 200%;
  height: 180px;
  animation: ${wave} 14s linear infinite;
  pointer-events: none;
`;

const bob = keyframes`
  0%, 100% { transform: translateY(0) rotate(-6deg); }
  50% { transform: translateY(-8px) rotate(6deg); }
`;

const WaterDuck = styled.div`
  position: absolute;
  z-index: 3;
  pointer-events: none;
  filter: drop-shadow(0 6px 10px rgba(10, 42, 74, 0.35));
  animation: ${bob} 4s ease-in-out infinite;
  transform-origin: 50% 100%;

  &.water-1 { left: 4%;  bottom: 64px;  width: 76px; animation-delay: 0s; }
  &.water-2 { left: 20%; bottom: 92px;  width: 58px; animation-delay: 0.7s; animation-duration: 3.4s; }
  &.water-3 { left: 36%; bottom: 56px;  width: 86px; animation-delay: 1.4s; animation-duration: 4.6s; }
  &.water-4 { left: 54%; bottom: 100px; width: 52px; animation-delay: 2s;   animation-duration: 3.8s; }
  &.water-5 { left: 70%; bottom: 62px;  width: 70px; animation-delay: 1s;   animation-duration: 4.2s; }
  &.water-6 { left: 86%; bottom: 90px;  width: 60px; animation-delay: 2.6s; animation-duration: 3.6s; }

  @media screen and (max-width: 768px) {
    &.water-1 { width: 52px; bottom: 46px; }
    &.water-2 { width: 42px; bottom: 68px; }
    &.water-3 { width: 58px; bottom: 40px; }
    &.water-4 { display: none; }
    &.water-5 { width: 48px; bottom: 48px; }
    &.water-6 { display: none; }
  }
`;

const Logo = styled(motion.img)`
  width: 140px;
  height: auto;
  margin-bottom: 1.5rem;
  filter: drop-shadow(0 10px 20px rgba(10, 42, 74, 0.18));

  @media screen and (max-width: 480px) {
    width: 110px;
  }
`;

const Eyebrow = styled.span`
  display: inline-block;
  background: rgba(10, 42, 74, 0.92);
  color: #ffce2d;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 0.5rem 1rem;
  border-radius: 999px;
  margin-bottom: 1.25rem;
`;

const Title = styled(motion.h1)`
  font-size: clamp(2.2rem, 6vw, 4.5rem);
  font-weight: 800;
  color: #0a2a4a;
  line-height: 1.05;
  max-width: 1100px;
  margin: 0 auto 1rem;
  text-shadow: 0 2px 0 rgba(255, 255, 255, 0.5);
`;

const Highlight = styled.span`
  background: linear-gradient(180deg, transparent 60%, #ffce2d 60%);
  padding: 0 0.25rem;
`;

const Subtitle = styled(motion.p)`
  font-size: clamp(1rem, 2.4vw, 1.35rem);
  color: #0a2a4a;
  opacity: 0.85;
  max-width: 720px;
  margin: 0 auto 2rem;
  line-height: 1.5;
  font-weight: 500;
`;

const ThankYou = styled(motion.div)`
  background: rgba(255, 255, 255, 0.65);
  border: 1px solid rgba(255, 206, 45, 0.6);
  border-radius: 20px;
  padding: 1.25rem 1.5rem;
  max-width: 640px;
  margin: 0 auto 2rem;
  backdrop-filter: blur(6px);
  box-shadow: 0 16px 32px -20px rgba(10, 42, 74, 0.35);

  strong {
    display: block;
    font-size: clamp(1.1rem, 3vw, 1.5rem);
    font-weight: 800;
    color: #0a2a4a;
    margin-bottom: 0.4rem;
  }

  p {
    margin: 0;
    font-size: clamp(0.92rem, 2.2vw, 1.05rem);
    color: #0a2a4a;
    opacity: 0.85;
    line-height: 1.55;
  }

  @media screen and (max-width: 480px) {
    padding: 1rem 1.1rem;
    border-radius: 16px;
  }
`;

const CTARow = styled(motion.div)`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  justify-content: center;
  margin-bottom: 2.5rem;
`;

const ClosedCTA = styled.div`
  background: rgba(250, 167, 30, 0.18);
  color: #b9740a;
  padding: 1rem 2rem;
  border: 1px dashed rgba(250, 167, 30, 0.7);
  border-radius: 999px;
  font-weight: 700;
  font-size: 1rem;
  cursor: default;
  user-select: none;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-family: inherit;
`;

const SecondaryCTA = styled(Link)`
  background: rgba(10, 42, 74, 0.9);
  color: #fff;
  padding: 1rem 2rem;
  border-radius: 999px;
  font-weight: 700;
  font-size: 1rem;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: transform 0.2s ease, background 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    background: #0a2a4a;
  }
`;

const Stats = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem 2.5rem;
  justify-content: center;
  margin-top: 1rem;
  z-index: 5;
  position: relative;
`;

const Stat = styled.div`
  text-align: center;
  color: #0a2a4a;

  strong {
    display: block;
    font-size: clamp(1.5rem, 3vw, 2rem);
    font-weight: 800;
    color: #0a2a4a;
  }

  span {
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    opacity: 0.7;
  }
`;

const ScrollHint = styled(Link)`
  position: absolute;
  bottom: 2.5rem;
  left: 50%;
  transform: translateX(-50%);
  color: #0a2a4a;
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  text-decoration: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  z-index: 5;
  opacity: 0.7;

  svg {
    animation: ${keyframes`
      0%, 100% { transform: translateY(0); }
      50% { transform: translateY(6px); }
    `} 1.6s ease-in-out infinite;
  }

  @media screen and (max-width: 480px) {
    display: none;
  }
`;

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.12 },
  }),
};

const Hero = () => {
  return (
    <Section>
      <Logo
        src={logoRata}
        alt="Cursa de Rățuște pe Bega"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6 }}
      />

      <Eyebrow>{RACE_DATE_LABEL} · {RACE_EDITION} · {RACE_CITY}</Eyebrow>

      <Title initial="hidden" animate="visible" custom={1} variants={fadeUp}>
        Cursa de Rățuște <Highlight>pe Bega</Highlight> 2026
      </Title>

      <Subtitle initial="hidden" animate="visible" custom={2} variants={fadeUp}>
        Adoptă o rățușcă, vino pe malurile Begăi de Ziua Copilului și
        sprijină Secția de Neonatologie a Spitalului „Louis Țurcanu”.
      </Subtitle>

      <ThankYou initial="hidden" animate="visible" custom={3} variants={fadeUp}>
        <strong>Mulțumim tuturor participanților! 🦆💛</strong>
        <p>
          Ați fost extraordinari! Datorită vouă, ediția din 2026 a fost un
          adevărat val de bine. Felicitări câștigătorilor și mulțumim că ați
          fost alături de noi! ✨
        </p>
      </ThankYou>

      <CTARow initial="hidden" animate="visible" custom={4} variants={fadeUp}>
        <ClosedCTA aria-disabled="true">
          🔒 Ne vedem la anul!
        </ClosedCTA>
        <SecondaryCTA to="/#castigatori">Vezi câștigătorii</SecondaryCTA>
      </CTARow>

      <Stats>
        <Stat>
          <strong>17</strong>
          <span>Ediții</span>
        </Stat>
        <Stat>
          <strong>10 RON</strong>
          <span>O rățușcă</span>
        </Stat>
        <Stat>
          <strong>100%</strong>
          <span>Caritabil</span>
        </Stat>
      </Stats>

      <DonationProgress />

      <ScrollHint to="/#countdown">
        Scroll <FiChevronDown size={22} />
      </ScrollHint>

      <Wave
        viewBox="0 0 1440 180"
        preserveAspectRatio="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M0,80 C240,160 480,0 720,80 C960,160 1200,0 1440,80 L1440,180 L0,180 Z"
          fill="#3DA5E8"
          opacity="0.5"
        />
        <path
          d="M0,110 C240,180 480,40 720,110 C960,180 1200,40 1440,110 L1440,180 L0,180 Z"
          fill="#1F7DB5"
        />
      </Wave>

      <WaterDuck className="water-1"><RubberDuck /></WaterDuck>
      <WaterDuck className="water-2"><RubberDuck flip /></WaterDuck>
      <WaterDuck className="water-3"><RubberDuck /></WaterDuck>
      <WaterDuck className="water-4"><RubberDuck flip /></WaterDuck>
      <WaterDuck className="water-5"><RubberDuck /></WaterDuck>
      <WaterDuck className="water-6"><RubberDuck flip /></WaterDuck>
    </Section>
  );
};

export default Hero;
